import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useSidebarState } from '../../hooks/useSidebarState';
import { calculateTableWidth } from '@/utils/tableWidth';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

export interface TableColumn {
  id: string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  headerSx?: SxProps<Theme>;
  borderRight?: boolean | string;
  sticky?: boolean | 'left' | 'right';
  stickyOffset?: number;
  stickyZIndex?: number;
  getRowSpan?: (row: any, rowIndex: number) => number | undefined;
  render?: (value: any, row: any) => React.ReactNode;
}

interface GroupHeaderCell {
  label: string;
  colSpan: number;
  align?: 'left' | 'center' | 'right';
}

interface DataTableModuleProps {
  data: any[];
  columns: TableColumn[];
  onRowSelect: (id: string) => void;
  onSelectAll: () => void;
  onRowClick?: (row: any) => void;
  getRowId: (row: any) => string;
  isRowSelected: (row: any) => boolean;
  areAllRowsSelected: boolean;
  areSomeRowsSelected: boolean;
  leftFilterWidth?: number;
  reserveLeftFilterSpace?: boolean;
  showCheckbox?: boolean;
  checkboxDisabled?: boolean;
  isCheckboxDisabledForRow?: (row: any) => boolean;
  hideCheckboxForRow?: (row: any) => boolean;
  getRowClassName?: (row: any) => string;
  groupHeaderRows?: GroupHeaderCell[][];
  enableRowDragDrop?: boolean;
  onRowReorder?: (activeRowId: string, overRowId: string, meta?: { dropInside: boolean; horizontalOffset: number }) => void;
  onRowDragHover?: (payload: { activeRowId: string; overRowId: string; horizontalOffset: number; dropInside: boolean } | null) => void;
  tableLayout?: 'auto' | 'fixed';
  tableContainerSx?: SxProps<Theme>;
  tableSx?: SxProps<Theme>;
  fullWidth?: boolean;
  stickyCheckbox?: boolean;
  checkboxColumnWidth?: number;
  checkboxCellPaddingX?: number;
}

export default function DataTableModule({
  data,
  columns,
  onRowSelect,
  onSelectAll,
  onRowClick,
  getRowId,
  isRowSelected,
  areAllRowsSelected,
  areSomeRowsSelected,
  leftFilterWidth = 260,
  reserveLeftFilterSpace = true,
  showCheckbox = true,
  checkboxDisabled = false,
  isCheckboxDisabledForRow,
  hideCheckboxForRow,
  getRowClassName,
  groupHeaderRows,
  enableRowDragDrop = false,
  onRowReorder,
  onRowDragHover,
  tableLayout = 'auto',
  tableContainerSx,
  tableSx,
  fullWidth = false,
  stickyCheckbox = false,
  checkboxColumnWidth = 50,
  checkboxCellPaddingX = 1,
}: DataTableModuleProps) {
  const [tableWidth, setTableWidth] = useState<number>(0);
  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
  const [dragArmedRowId, setDragArmedRowId] = useState<string | null>(null);
  const [pointerDownX, setPointerDownX] = useState<number | null>(null);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);
  const [dropInsideRowId, setDropInsideRowId] = useState<string | null>(null);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const tableDragScrollRef = React.useRef({
    active: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
  });
  const suppressTableClickRef = React.useRef(false);
  const [isTableDragScrolling, setIsTableDragScrolling] = useState(false);
  const sidebarOpen = useSidebarState();

  const fixedColumnsWidth = React.useMemo(() => columns.reduce((total, col) => {
    if (!col.width || col.width.trim().toLowerCase() === 'auto') return total;
    const parsed = parseInt(col.width, 10);
    return total + (Number.isFinite(parsed) ? parsed : 0);
  }, 0), [columns]);

  const hasAutoColumns = React.useMemo(
    () => columns.some((col) => typeof col.width === 'string' && col.width.trim().toLowerCase() === 'auto'),
    [columns]
  );

  const getColumnWidthPx = React.useCallback((column: TableColumn, fallback = 80): number => {
    if (!column.width || column.width.trim().toLowerCase() === 'auto') return fallback;
    const parsed = parseInt(column.width, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }, []);

  const stickyLeftOffsets = React.useMemo(() => {
    const offsets = new Map<number, number>();
    let currentLeft = showCheckbox ? checkboxColumnWidth : 0;

    columns.forEach((column, index) => {
      if (column.sticky === true || column.sticky === 'left') {
        const offset = typeof column.stickyOffset === 'number' ? column.stickyOffset : currentLeft;
        offsets.set(index, offset);
        currentLeft = offset + getColumnWidthPx(column);
      }
    });

    return offsets;
  }, [columns, getColumnWidthPx, showCheckbox]);

  const stickyRightOffsets = React.useMemo(() => {
    const offsets = new Map<number, number>();
    let currentRight = 0;

    for (let index = columns.length - 1; index >= 0; index -= 1) {
      const column = columns[index];
      if (column.sticky === 'right') {
        const offset = typeof column.stickyOffset === 'number' ? column.stickyOffset : currentRight;
        offsets.set(index, offset);
        currentRight = offset + getColumnWidthPx(column);
      }
    }

    return offsets;
  }, [columns, getColumnWidthPx]);

  const getStickyCellStyles = React.useCallback(
    (column: TableColumn, columnIndex: number, isHeader: boolean, rowBackground?: string, isGroupRow = false) => {
      const isStickyLeft = stickyLeftOffsets.has(columnIndex);
      const isStickyRight = stickyRightOffsets.has(columnIndex);
      if (!isStickyLeft && !isStickyRight) return {};

      const baseZIndex = typeof column.stickyZIndex === 'number' ? column.stickyZIndex : (isHeader ? 6 : 3);

      return {
        position: 'sticky',
        ...(isStickyLeft ? { left: stickyLeftOffsets.get(columnIndex) } : {}),
        ...(isStickyRight ? { right: stickyRightOffsets.get(columnIndex) } : {}),
        zIndex: baseZIndex,
        backgroundColor: rowBackground || (isHeader ? '#f9fafb' : '#fff'),
        ...(!isGroupRow && isStickyLeft ? { boxShadow: '1px 0 0 #e5e7eb' } : {}),
        ...(!isGroupRow && isStickyRight ? { boxShadow: '-1px 0 0 #e5e7eb' } : {}),
      };
    },
    [stickyLeftOffsets, stickyRightOffsets]
  );

  const getStickyCheckboxCellStyles = React.useCallback(
    (isHeader: boolean, rowBackground?: string) => {
      if (!stickyCheckbox) return {};

      return {
        position: 'sticky',
        left: 0,
        zIndex: isHeader ? 7 : 4,
        backgroundColor: rowBackground || (isHeader ? '#f9fafb' : '#fff'),
        boxShadow: '1px 0 0 #e5e7eb',
      };
    },
    [stickyCheckbox]
  );

  const shouldShowCheckboxForRow = React.useCallback(
    (row: any) => showCheckbox && !row?.__hideCheckbox && !(hideCheckboxForRow?.(row) ?? false),
    [hideCheckboxForRow, showCheckbox]
  );

  const stickyLeftColumnCount = React.useMemo(
    () => columns.filter((column) => column.sticky === true || column.sticky === 'left').length,
    [columns]
  );

  const isInteractiveTarget = (target: EventTarget | null): boolean => {
    const element = target as HTMLElement | null;
    if (!element || typeof element.closest !== 'function') return false;

    return Boolean(
      element.closest(
        'input, textarea, button, a, [role="button"], [role="option"], [role="listbox"], [role="combobox"], .MuiAutocomplete-root, .MuiAutocomplete-popper, .MuiSelect-root, .MuiChip-root, .MuiCheckbox-root'
      )
    );
  };

  const isDragHandleTarget = (target: EventTarget | null): boolean => {
    const element = target as HTMLElement | null;
    if (!element || typeof element.closest !== 'function') return false;
    return Boolean(element.closest('[data-row-drag-handle="true"]'));
  };

  const stopTableDragScroll = React.useCallback(() => {
    if (!tableDragScrollRef.current.active) return;

    tableDragScrollRef.current.active = false;
    setIsTableDragScrolling(false);
  }, []);

  useEffect(() => {
    if (!isTableDragScrolling) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const scrollState = tableDragScrollRef.current;
      const container = tableContainerRef.current;
      if (!scrollState.active || !container) return;

      const deltaX = event.clientX - scrollState.startX;
      if (Math.abs(deltaX) > 3) {
        scrollState.moved = true;
        suppressTableClickRef.current = true;
      }

      container.scrollLeft = scrollState.scrollLeft - deltaX;
      event.preventDefault();
    };

    const handleMouseUp = () => {
      stopTableDragScroll();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isTableDragScrolling, stopTableDragScroll]);

  useEffect(() => {
    const handleResize = () => {
      const finalWidth = calculateTableWidth({
        windowWidth: window.innerWidth,
        sidebarOpen,
        leftFilterWidth: reserveLeftFilterSpace ? leftFilterWidth : 0,
      });
      setTableWidth(finalWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen, leftFilterWidth, reserveLeftFilterSpace]);

  const groupBoundaryColumnIndexes = React.useMemo(() => {
    if (!Array.isArray(groupHeaderRows) || groupHeaderRows.length === 0 || groupHeaderRows[0].length <= 1) {
      return new Set<number>();
    }

    const topGroupRow = groupHeaderRows[0];
    const boundaryIndexes = new Set<number>();
    let cumulativeColumnSpan = 0;

    topGroupRow.forEach((groupCell, groupIndex) => {
      cumulativeColumnSpan += Math.max(groupCell.colSpan || 0, 0);
      if (groupIndex < topGroupRow.length - 1) {
        boundaryIndexes.add(Math.max(cumulativeColumnSpan - 1, 0));
      }
    });

    return boundaryIndexes;
  }, [groupHeaderRows]);



  const tableRowStyles = (row: any) => {
    const isSelected = isRowSelected(row);
    const baseStyles = {
      ...(sharedListStyles.timesheetListTable.row(isSelected ? '#f0f9ff' : '#fff', false) as object),
      cursor: 'pointer',
      '&:hover td': {
        backgroundColor: isSelected ? '#e0f2fe' : '#f8fafc',
      },
      '&:last-child td': {
        borderBottom: 0,
      },
    };

    // Add custom row styling if getRowClassName is provided
    if (getRowClassName) {
      const className = getRowClassName(row);
      const classTokens = String(className || '').split(/\s+/).filter(Boolean);
      const hasClass = (name: string) => classTokens.includes(name);

      if (hasClass('changed-row')) {
        return {
          ...baseStyles,
          backgroundColor: isRowSelected(row) ? '#f0f9ff' : '#e3f2fd',
          '&:hover': {
            backgroundColor: isRowSelected(row) ? '#e0f2fe' : '#bbdefb',
          },
        };
      }

      if (hasClass('blocked-row')) {
        return {
          ...baseStyles,
          backgroundColor: '#ecfdf5',
          '& td': {
            backgroundColor: '#ecfdf5',
          },
          '&:hover td': {
            backgroundColor: '#dcfce7',
          },
        };
      }

      if (hasClass('group-row')) {
        return {
          ...baseStyles,
          cursor: hasClass('group-row-clickable') ? 'pointer' : 'default',
          backgroundColor: '#eef2ff',
          '&:hover': {
            backgroundColor: '#e0e7ff',
          },
          '& td': {
            background: 'linear-gradient(90deg, #eef2ff 0%, #f5f3ff 100%)',
            color: '#312e81',
            fontWeight: 700,
            borderBottom: '1px solid #c7d2fe',
          },
          '& td:first-of-type': {
            borderLeft: '4px solid #6366f1',
          },
        };
      }

      if (hasClass('item-row-indented')) {
        const indentedStyles = {
          ...baseStyles,
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
        };

        if (hasClass('drag-parent-highlight')) {
          return {
            ...indentedStyles,
            '& td': {
              backgroundColor: '#ecfeff',
            },
            '& td:first-of-type': {
              boxShadow: 'inset 4px 0 0 #0891b2',
            },
          };
        }

        return indentedStyles;
      }

      if (hasClass('month-group-even')) {
        return {
          ...baseStyles,
          '& td': {
            backgroundColor: '#fafafa',
          },
          '&:hover td': {
            backgroundColor: '#f4f4f5',
          },
        };
      }

      if (hasClass('month-group-odd')) {
        return {
          ...baseStyles,
          '& td': {
            backgroundColor: '#f5f9ff',
          },
          '&:hover td': {
            backgroundColor: '#edf4ff',
          },
        };
      }
    }

    return baseStyles;
  };

 

  return (
    <Box className='data-table-module' sx={sharedListStyles.timesheetListTable.wrapper}>
        <TableContainer
          ref={tableContainerRef}
          onMouseDown={(event) => {
            if (event.button !== 0) return;
            if (isInteractiveTarget(event.target) || isDragHandleTarget(event.target)) return;

            const container = tableContainerRef.current;
            if (!container || container.scrollWidth <= container.clientWidth) return;

            tableDragScrollRef.current = {
              active: true,
              moved: false,
              startX: event.clientX,
              scrollLeft: container.scrollLeft,
            };
            suppressTableClickRef.current = false;
            setIsTableDragScrolling(true);
            event.preventDefault();
          }}
          onClickCapture={(event) => {
            if (!suppressTableClickRef.current) return;

            suppressTableClickRef.current = false;
            event.preventDefault();
            event.stopPropagation();
          }}
          sx={{
            ...(sharedListStyles.timesheetListTable.tableContainer(tableWidth) as object),
            maxHeight: 'none',
            overflowY: 'hidden',
            paddingBottom: 0,
            width: fullWidth ? '100%' : 'fit-content',
            minWidth: fullWidth ? '100%' : undefined,
            maxWidth: fullWidth ? '100%' : (tableWidth > 0 ? `${tableWidth}px` : '100%'),
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
            cursor: isTableDragScrolling ? 'grabbing' : 'grab',
            userSelect: isTableDragScrolling ? 'none' : undefined,
            ...(tableContainerSx as object),
          }}
        >
          <Table
            size="small"
            sx={{
              ...(sharedListStyles.timesheetListTable.table as object),
              tableLayout,
              ...(tableLayout === 'fixed' && !hasAutoColumns ? {
                width: `${fixedColumnsWidth + (showCheckbox ? checkboxColumnWidth : 0)}px`,
                minWidth: `${fixedColumnsWidth + (showCheckbox ? checkboxColumnWidth : 0)}px`,
              } : {
                width: 'max-content',
                minWidth: 'max-content',
              }),
              ...(fullWidth ? {
                width: '100%',
                minWidth: '100%',
              } : {}),
              ...(tableSx as object),
              borderCollapse: 'separate',
              borderSpacing: 0,
            }}
          >
          <TableHead>
            {Array.isArray(groupHeaderRows) && groupHeaderRows.map((groupRow, groupRowIndex) => (
              <TableRow
                key={`group-header-row-${groupRowIndex}`}
                sx={{
                  '& > th': {
                    borderBottom: '1px solid #d1d5db',
                  },
                }}
              >
                {showCheckbox && (
                  <TableCell
                    sx={sharedListStyles.timesheetListTable.headerCell(checkboxColumnWidth, -1, checkboxColumnWidth)}
                  />
                )}
                {groupRow.map((groupCell, groupCellIndex) => (
                  <TableCell
                    key={`group-header-cell-${groupRowIndex}-${groupCellIndex}`}
                    colSpan={groupCell.colSpan}
                    align={groupCell.align || 'center'}
                    sx={{
                      ...(sharedListStyles.timesheetListTable.headerCell(80, -1, checkboxColumnWidth) as object),
                      textAlign: groupCell.align || 'center',
                      backgroundColor: '#f3f4f6',
                      borderBottom: '1px solid #ced8ea !important',
                      ...(groupCellIndex < groupRow.length - 1
                        ? { borderRight: '1px solid #e5e7eb' }
                        : {}),
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {groupCell.label}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              {showCheckbox && (
                <TableCell
                  sx={{
                    ...(sharedListStyles.timesheetListTable.headerCell(checkboxColumnWidth, -1, checkboxColumnWidth) as object),
                    ...(getStickyCheckboxCellStyles(true) as object),
                    px: checkboxCellPaddingX,
                  }}
                >
                  <Checkbox
                    checked={areAllRowsSelected}
                    indeterminate={areSomeRowsSelected && !areAllRowsSelected}
                    onChange={onSelectAll}
                    size="small"
                    disabled={checkboxDisabled}
                  />
                </TableCell>
              )}
               {columns.map((column, columnIndex) => {
                 const explicitBorderRight = column.borderRight
                   ? (typeof column.borderRight === 'string' ? column.borderRight : '1px solid #e5e7eb')
                   : null;

                 return (
                 <TableCell 
                   key={column.id}
                   align={column.headerAlign || column.align}
                   sx={{
                     ...(sharedListStyles.timesheetListTable.headerCell(
                       column.width ? parseInt(column.width, 10) : 80,
                       -1,
                       checkboxColumnWidth
                     ) as object),
                     ...(getStickyCellStyles(column, columnIndex, true) as object),
                     width: column.width || 'auto',
                     textAlign: column.headerAlign || column.align || 'left',
                     whiteSpace: 'nowrap',
                     overflow: 'hidden',
                     textOverflow: 'ellipsis',
                     textTransform: 'none',
                     ...(explicitBorderRight ? { borderRight: explicitBorderRight } : {}),
                     ...(groupBoundaryColumnIndexes.has(columnIndex)
                       ? { borderRight: '1px solid #e5e7eb' }
                       : {}),
                     '&:last-of-type': {
                       borderRight: explicitBorderRight || '1px solid #e5e7eb',
                     },
                     ...(column.headerSx as object),
                   }}
                 >
                   {column.label}
                 </TableCell>
                 );
               })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={getRowId(row) || `row-${index}`}
                draggable={enableRowDragDrop}
                onMouseDown={(event) => {
                  if (!enableRowDragDrop) {
                    return;
                  }
                  const rowId = getRowId(row);
                  if (!isDragHandleTarget(event.target)) {
                    setDragArmedRowId(null);
                    return;
                  }
                  if (isInteractiveTarget(event.target)) {
                    setDragArmedRowId(null);
                    return;
                  }
                  setDragArmedRowId(rowId);
                  setPointerDownX(event.clientX);
                }}
                onDragStart={(event) => {
                  if (!enableRowDragDrop) {
                    return;
                  }
                  const rowId = getRowId(row);
                  if (dragArmedRowId !== rowId) {
                    event.preventDefault();
                    return;
                  }
                  if (isInteractiveTarget(event.target)) {
                    event.preventDefault();
                    return;
                  }
                  setDraggingRowId(rowId);
                  setDragStartX(pointerDownX ?? event.clientX);
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', rowId);
                }}
                onDragOver={(event) => {
                  if (!enableRowDragDrop || !draggingRowId) {
                    return;
                  }
                  const overRowId = getRowId(row);
                  event.preventDefault();
                  const deltaX = dragStartX === null ? 0 : event.clientX - dragStartX;
                  setDragOverRowId(overRowId);
                  const dropInside = deltaX > 28;
                  setDropInsideRowId(dropInside ? overRowId : null);
                  onRowDragHover?.({
                    activeRowId: draggingRowId,
                    overRowId,
                    horizontalOffset: deltaX,
                    dropInside,
                  });
                  event.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(event) => {
                  if (!enableRowDragDrop || !draggingRowId) {
                    return;
                  }
                  event.preventDefault();
                  const overRowId = getRowId(row);
                  const horizontalOffset = dragStartX === null ? 0 : event.clientX - dragStartX;
                  const isDropInside = dropInsideRowId === overRowId;
                  const isDropOutsideLeft = horizontalOffset < -28;
                  if (overRowId !== draggingRowId || isDropInside || isDropOutsideLeft) {
                    onRowReorder?.(draggingRowId, overRowId, { dropInside: isDropInside, horizontalOffset });
                  }
                  setDraggingRowId(null);
                  setPointerDownX(null);
                  setDragArmedRowId(null);
                  setDragStartX(null);
                  setDragOverRowId(null);
                  setDropInsideRowId(null);
                  onRowDragHover?.(null);
                }}
                onDragEnd={() => {
                  if (draggingRowId) {
                    setDraggingRowId(null);
                  }
                  setPointerDownX(null);
                  setDragArmedRowId(null);
                  setDragStartX(null);
                  setDragOverRowId(null);
                  setDropInsideRowId(null);
                  onRowDragHover?.(null);
                }}
                onClick={(e) => {
                  if (isInteractiveTarget(e.target)) return;
                  onRowClick?.(row);
                }}
                sx={{
                  ...tableRowStyles(row),
                  cursor: enableRowDragDrop ? 'grab' : (tableRowStyles(row) as any)?.cursor,
                  opacity: draggingRowId && draggingRowId === getRowId(row) ? 0.5 : 1,
                  ...(dragOverRowId === getRowId(row)
                    ? {
                        backgroundColor: dropInsideRowId === getRowId(row) ? '#e0f2fe' : '#f8fafc',
                        '& td:first-of-type': dropInsideRowId === getRowId(row)
                          ? {
                              boxShadow: 'inset 4px 0 0 #0284c7',
                            }
                          : undefined,
                      }
                    : {}),
                }}
              >
                {row?.__fullWidthRow ? (
                  <>
                    {shouldShowCheckboxForRow(row) && (
                      <TableCell
                        sx={{
                          ...(sharedListStyles.timesheetListTable.bodyCell as object),
                          minWidth: checkboxColumnWidth,
                          width: checkboxColumnWidth,
                          maxWidth: checkboxColumnWidth,
                          px: checkboxCellPaddingX,
                          ...(getStickyCheckboxCellStyles(false, isRowSelected(row) ? '#f0f9ff' : '#fff') as object),
                        }}
                      >
                        <Checkbox
                          checked={isRowSelected(row)}
                          onChange={() => onRowSelect(getRowId(row))}
                          size="small"
                          disabled={checkboxDisabled || Boolean(isCheckboxDisabledForRow?.(row))}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      colSpan={columns.length + (shouldShowCheckboxForRow(row) ? 0 : (showCheckbox ? 1 : 0))}
                      sx={{
                        ...(sharedListStyles.timesheetListTable.bodyCell as object),
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'clip',
                      }}
                    >
                      {columns[0]?.render
                        ? columns[0].render(row[columns[0].id], row)
                        : row[columns[0]?.id]}
                    </TableCell>
                  </>
                ) : row?.__groupRow ? (
                  <>
                    {(shouldShowCheckboxForRow(row) || (showCheckbox && row?.__hideCheckbox)) && (
                      <TableCell
                        sx={{
                          ...(sharedListStyles.timesheetListTable.bodyCell as object),
                          minWidth: checkboxColumnWidth,
                          width: checkboxColumnWidth,
                          maxWidth: checkboxColumnWidth,
                          px: checkboxCellPaddingX,
                          ...(getStickyCheckboxCellStyles(false, '#eef2ff') as object),
                          borderRight: 'none !important',
                        }}
                      >
                        {shouldShowCheckboxForRow(row) && (
                          <Checkbox
                            checked={isRowSelected(row)}
                            onChange={() => onRowSelect(getRowId(row))}
                            size="small"
                            disabled={checkboxDisabled || Boolean(isCheckboxDisabledForRow?.(row))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </TableCell>
                    )}
                    {(() => {
                      const totalLogicalColumns = columns.length + (showCheckbox ? 1 : 0);
                      const renderedCheckboxColumns = shouldShowCheckboxForRow(row) || (showCheckbox && row?.__hideCheckbox) ? 1 : 0;
                      const contentColumns = Math.max(totalLogicalColumns - renderedCheckboxColumns, 1);
                      const stickySpan = Math.max(
                        1,
                        Math.min(contentColumns, stickyLeftColumnCount)
                      );
                      const fillerSpan = contentColumns - stickySpan;
                      const stickyWidth = columns.slice(0, stickySpan).reduce((total, col) => total + getColumnWidthPx(col), 0);

                      return (
                        <>
                          <TableCell
                            colSpan={stickySpan}
                            sx={{
                              ...(sharedListStyles.timesheetListTable.bodyCell as object),
                              position: 'sticky',
                              left: showCheckbox && stickyCheckbox ? checkboxColumnWidth : 0,
                              zIndex: 5,
                              backgroundColor: '#eef2ff',
                              boxShadow: '1px 0 0 #e5e7eb',
                              borderRight: 'none !important',
                              whiteSpace: 'normal',
                              overflow: 'visible',
                              textOverflow: 'clip',
                              minWidth: stickyWidth,
                              width: stickyWidth,
                            }}
                          >
                            {columns[0]?.render
                              ? columns[0].render(row[columns[0].id], row)
                              : row[columns[0]?.id]}
                          </TableCell>
                          {row?.__renderGroupCells ? (
                            columns.slice(stickySpan).map((column) => {
                              const hasCellValue = Object.prototype.hasOwnProperty.call(row, column.id);
                              const explicitBorderRight = column.borderRight
                                ? (typeof column.borderRight === 'string' ? column.borderRight : '1px solid #e5e7eb')
                                : null;

                              return (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={{
                                    ...(sharedListStyles.timesheetListTable.bodyCell as object),
                                    minWidth: getColumnWidthPx(column),
                                    width: column.width || 'auto',
                                    textAlign: column.align || 'left',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    backgroundColor: '#eef2ff',
                                    ...(explicitBorderRight ? { borderRight: explicitBorderRight } : {}),
                                    '&:last-of-type': {
                                      borderRight: explicitBorderRight || '1px solid #f3f4f6',
                                    },
                                  }}
                                >
                                  {hasCellValue
                                    ? (column.render ? column.render(row[column.id], row) : row[column.id])
                                    : null}
                                </TableCell>
                              );
                            })
                          ) : (
                            fillerSpan > 0 && (
                              <TableCell
                                colSpan={fillerSpan}
                                sx={{
                                  ...(sharedListStyles.timesheetListTable.bodyCell as object),
                                  backgroundColor: '#eef2ff',
                                  borderRight: 'none !important',
                                }}
                              >
                                {(() => {
                                  const lastColumn = columns[columns.length - 1];
                                  if (!lastColumn?.render) return null;
                                  const trailingContent = lastColumn.render(
                                    row[lastColumn.id],
                                    row,
                                  );
                                  if (!trailingContent) return null;
                                  return (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        width: '100%',
                                      }}
                                    >
                                      {trailingContent}
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                            )
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    {shouldShowCheckboxForRow(row) && (
                      <TableCell
                        sx={{
                          ...(sharedListStyles.timesheetListTable.bodyCell as object),
                          minWidth: checkboxColumnWidth,
                          width: checkboxColumnWidth,
                          maxWidth: checkboxColumnWidth,
                          px: checkboxCellPaddingX,
                          ...(getStickyCheckboxCellStyles(false, isRowSelected(row) ? '#f0f9ff' : '#fff') as object),
                        }}
                      >
                        <Checkbox
                          checked={isRowSelected(row)}
                          onChange={() => onRowSelect(getRowId(row))}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    {columns.map((column, columnIndex) => {
                      const rowSpan = column.getRowSpan?.(row, index);
                      if (typeof rowSpan === 'number' && rowSpan <= 0) {
                        return null;
                      }

                      const explicitBorderRight = column.borderRight
                        ? (typeof column.borderRight === 'string' ? column.borderRight : '1px solid #e5e7eb')
                        : null;
                      const isGroupRow = Boolean((row as any)?.__groupRow);
                      const rowBackground = isRowSelected(row) ? '#f0f9ff' : '#fff';

                      return (
                      <TableCell 
                        key={column.id}
                        align={column.align}
                        rowSpan={typeof rowSpan === 'number' && rowSpan > 1 ? rowSpan : undefined}
                        sx={{
                          ...(sharedListStyles.timesheetListTable.bodyCell as object),
                          ...(getStickyCellStyles(column, columnIndex, false, rowBackground, isGroupRow) as object),
                          minWidth: getColumnWidthPx(column),
                          width: column.width || 'auto',
                          textAlign: column.align || 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(isGroupRow ? { borderRight: 'none !important' } : {}),
                          ...(!isGroupRow && explicitBorderRight ? { borderRight: explicitBorderRight } : {}),
                          ...(!isGroupRow && groupBoundaryColumnIndexes.has(columnIndex)
                            ? { borderRight: '1px solid #e5e7eb' }
                            : {}),
                          '&:last-of-type': {
                            borderRight: isGroupRow ? 'none !important' : (explicitBorderRight || '1px solid #f3f4f6'),
                          },
                        }}
                      >
                        {column.render ? column.render(row[column.id], row) : row[column.id]}
                      </TableCell>
                      );
                    })}
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
